import { useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Button, Form, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { RichTextEditor } from '@factorialco/factorial-one-react/dist/experimental'
import {
  AtsCreateFeedbackNewDocument,
  AtsFeedbackNewApplicationFragment,
  AtsJobApplicatioNotesDocument,
  GetAtsFeedbacksDocument,
} from 'generated/resources/graphql'
import { AtsFeedbackNewFeaturesDocument } from 'generated/resources/graphql'

import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { RatingSelector } from 'modules/ats/components/Feedbacks/RatingSelector'
import { AtsFeedbackFormValues } from 'modules/ats/lib/utils/feedbacks'
import {
  useNotesMentions,
  useRichTextEditorNotesMentions,
} from 'modules/ats/lib/utils/useNotesMentions'

interface Props {
  candidateId: number
  application?: AtsFeedbackNewApplicationFragment
  draftNote: string | undefined | null
  onDraftChange: (draft: string) => void
  closeNote: () => void
  enabledScoring?: boolean
}

interface AtsFeedbackCreateFormValues extends AtsFeedbackFormValues {
  accessId: number
  visibility: string
}

interface OutletContext {
  isReviewerRole: boolean
}

export const FeedbackNew = ({
  application,
  candidateId,
  closeNote,
  draftNote,
  onDraftChange,
  enabledScoring = true,
}: Props) => {
  const { company } = useCurrentSession()
  const { editorConfig, canUseRichTextEditor, loading: richTextEditorLoading } = useRichTextEditor()
  const { data: featuresData, loading: featuresLoading } = useQuery(
    AtsFeedbackNewFeaturesDocument,
    {
      variables: {
        companyId: company.id,
      },
    }
  )
  const { isReviewerRole } = useOutletContext<OutletContext>()

  const privateCommentsAvailable =
    featuresData?.companies.featureAvailabilitiesConnection.nodes.find(
      (node) => node.id === 'ats_customized_features'
    )?.available

  const hiringRolesAvailable = featuresData?.features.featuresConnection.nodes.find(
    (node) => node.name === 'dev_ats_hiring_manager_roles'
  )?.enabled

  const [createFeedback, { loading }] = useMutation(AtsCreateFeedbackNewDocument, {
    extractorFn: (data) => data.atsMutations.createAtsFeedback,
    refetchQueries: [GetAtsFeedbacksDocument, AtsJobApplicatioNotesDocument],
  })
  const editorRef = useRef<EditorHandle>(null)
  const [accessesIds, setAccessesIds] = useState<number[] | null>([])
  const uniqueMentioningAccesses = useNotesMentions({
    atsJobPostingId: application?.jobPosting.id,
    candidateId,
  })
  const {
    accesses: uniqueRichTextEditorMentioningAccesses,
    loading: richTextEditorMentionsLoading,
  } = useRichTextEditorNotesMentions({
    atsJobPostingId: application?.jobPosting.id,
    candidateId,
  })

  const { access } = useCurrentSession()
  const form = useForm<AtsFeedbackCreateFormValues>({
    defaultValues: {
      accessId: access.id,
      description: draftNote || '',
      rating: null,
      atsCandidateId: candidateId,
      atsApplicationId: application?.id,
      atsApplicationPhaseId: application?.applicationPhase.id,
      attachments: [],
      mentionIds: accessesIds,
      visibility: 'public',
    },
    async onSubmit(data, { setErrors }) {
      const result = await createFeedback({
        variables: {
          ...data,
          attachments: data.attachments,
          mentionIds: accessesIds,
        },
      })

      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onDraftChange('')
        setAccessesIds(null)
        form.resetForm()
        closeNote()
      }

      return handled
    },
  })

  // TODO: Make files updates & delete work
  const onFiles = useUnsafeCallback(
    (attachments) => {
      form.setFieldValue('attachments', attachments)
    },
    [form]
  )

  const privateCommentsEnabled = privateCommentsAvailable && hiringRolesAvailable && !isReviewerRole

  return (
    <Form onSubmit={() => form.handleSubmit()} padding={null}>
      <Box flexDirection="row" gap="s32" justifyContent="spaceBetween" alignItems="center">
        <Box gap="s8" flexDirection="row" alignItems="center" maxWidth="s600">
          <UserAvatar user={access} size="s40" />
          {enabledScoring && (
            <RatingSelector
              value={Number(form.values.rating)}
              onChange={(rating) => form.setFieldValue('rating', rating)}
            />
          )}
        </Box>
        {canUseRichTextEditor && privateCommentsEnabled && (
          <TogglerCheckbox
            value={form.values.visibility === 'private'}
            label={i18n.t('ats.feedbacks.private_comment.label')}
            onChange={() =>
              form.setFieldValue(
                'visibility',
                form.values.visibility === 'private' ? 'public' : 'private'
              )
            }
            information={i18n.t('ats.feedbacks.private_comment.tooltip')}
          />
        )}
      </Box>

      {richTextEditorLoading || richTextEditorMentionsLoading ? (
        <RichTextEditor.Skeleton rows={1} />
      ) : canUseRichTextEditor ? (
        <RichTextEditor
          {...editorConfig}
          title={i18n.t('ats.feedbacks.title_new')}
          onChange={(data) => {
            form.setFieldValue('description', data?.value)
            setAccessesIds(data?.mentionIds || null)
            if (data?.value) {
              onDraftChange(data?.value)
            }
          }}
          placeholder={i18n.t('ats.mentions.placeholder')}
          maxCharacters={5000}
          filesConfig={{
            onFiles: onFiles,
            multipleFiles: true,
          }}
          mentionsConfig={{
            users: uniqueRichTextEditorMentioningAccesses,
          }}
          primaryAction={{
            action: {
              label: i18n.t('common.save'),
              onClick: () => form.handleSubmit(),
              variant: 'default',
            },
          }}
          initialEditorState={{
            content: form.values.description || '',
          }}
          secondaryAction={[
            {
              label: i18n.t('common.cancel'),
              onClick: () => closeNote(),
              variant: 'outline',
              type: 'button',
            },
          ]}
        />
      ) : (
        <>
          <TextEditor
            ref={editorRef}
            onChange={(data) => {
              if (data && typeof data !== 'string') {
                form.setFieldValue('description', data.value)
                setAccessesIds(data.ids)
                onDraftChange(data.value)
              }
            }}
            onFiles={onFiles}
            placeholder={i18n.t('ats.mentions.placeholder')}
            initialEditorState={{
              data: form.values.description,
            }}
            height="s200"
            hasMentions
            users={uniqueMentioningAccesses}
            enableBetaFeatures={false}
          />

          <Box flexDirection="row" gap="s16" justifyContent="flexEnd">
            {!featuresLoading && privateCommentsEnabled && (
              <TogglerCheckbox
                value={form.values.visibility === 'private'}
                label={i18n.t('ats.feedbacks.private_comment.label')}
                onChange={() =>
                  form.setFieldValue(
                    'visibility',
                    form.values.visibility === 'private' ? 'public' : 'private'
                  )
                }
                information={i18n.t('ats.feedbacks.private_comment.tooltip')}
              />
            )}
            <Button type="secondary" onClick={() => closeNote()}>
              {i18n.t('common.cancel')}
            </Button>
            <Button disabled={loading || !form.isValid} submit>
              {i18n.t('common.submit')}
            </Button>
          </Box>
        </>
      )}
    </Form>
  )
}
